import {
  type ApiObject,
  type IdentifyTraits,
  RudderAnalytics,
} from "@rudderstack/analytics-js";
import { useEffect } from "preact/hooks";
import { rudderstackDataPlaneUrl, rudderstackWriteKey } from "@/config";
import { keysToSnake, toSnake } from "@/helpers/humps";
import type { AnalyticsEventType } from "@/types/analyticsEvents";
import { AnalyticsEvent } from "@/types/analyticsEvents";
import { getCookie } from "@/util/cookie";
import { ensureGoogleAdsCookies } from "@/util/googleAdsCookies";
import { ensureMetaCookies, getMetaTraits } from "@/util/metaPixelCookies";
import { getOpprefTrait } from "@/util/openaiCookies";
import User from "@/util/userUtil";

export type { AnalyticsEventType };
export { AnalyticsEvent };

// Module-level singleton so every hook consumer shares one client.
let rudderanalytics: RudderAnalytics | undefined;

type WindowWithRudder = typeof window & { rudderanalytics?: RudderAnalytics };

const getClient = (): RudderAnalytics | undefined => {
  if (typeof window === "undefined") return undefined;

  const existing = (window as WindowWithRudder).rudderanalytics;
  if (existing) {
    rudderanalytics = existing;
    return rudderanalytics;
  }
  return rudderanalytics;
};

export const useAnalytics = () => {
  const visitorCode = getCookie("kameleoonVisitorCode");

  const user = User.isLoggedIn() && User.hasUserData() && User.userData();

  useEffect(() => {
    if (typeof window === "undefined" || process.env.NODE_ENV === "test")
      return;

    // Persist the ad click ids before any event fires so the first page()
    // already has them and they survive navigation away from the landing URL.
    ensureMetaCookies();
    ensureGoogleAdsCookies();

    if ((window as WindowWithRudder).rudderanalytics) {
      rudderanalytics = (window as WindowWithRudder).rudderanalytics;
      return;
    }

    const instance = new RudderAnalytics();
    instance.load(rudderstackWriteKey, rudderstackDataPlaneUrl, {
      anonymousIdOptions: {
        autoCapture: { enabled: true, source: "segmentio" },
      },
    });

    // No page() here: RouteChangeHandler sends the first one.
    (window as WindowWithRudder).rudderanalytics = instance;
    rudderanalytics = instance;
  }, []);

  const track = (event: AnalyticsEventType, trackProperties: object = {}) => {
    const client = getClient();
    if (!client) return;

    // Callers pass their own narrowly-typed property bags (none of which
    // declare an index signature), so this is widened once here rather than
    // forcing every call site to shape its properties around this hook.
    const properties = trackProperties as Record<string, unknown>;

    properties.platform = "Web";
    if (event === AnalyticsEvent.CtaTapped && !properties.url) {
      properties.url = window.location.href;
    }

    if (visitorCode) {
      properties["k_visitorCode"] = visitorCode;
    }

    // Meta Pixel deduplication rides on properties.event_id (the device-mode FB
    // Pixel uses it as the Meta eventID). Callers that need client/server dedup
    // set it explicitly; no messageId override is required.

    const metaTraits = { ...getMetaTraits(), ...getOpprefTrait() };

    const userTrack =
      User.isLoggedIn() && User.hasUserData() && User.userData();
    if (userTrack) {
      const { id, firstName, lastName, email, subscription } = userTrack;

      const traits: {
        id: number;
        firstName: string;
        lastName: string;
        trialStartDate: string | null | undefined;
        trialEndDate: string | null | undefined;
        subscriptionStartDate: string | undefined;
        subscriptionPlanName: string | undefined;
        email?: string;
        k_visitorCode?: string;
      } = {
        id,
        firstName,
        lastName,
        trialStartDate: subscription?.trialStartedAt,
        trialEndDate: subscription?.trialEndsAt,
        subscriptionStartDate: subscription?.currentPeriodStartedAt,
        subscriptionPlanName: subscription?.plan?.name,
      };

      if (
        event === AnalyticsEvent.Subscribed ||
        event === AnalyticsEvent.SignedUp
      ) {
        traits["email"] = email;
      }

      if (visitorCode) {
        traits["k_visitorCode"] = visitorCode;
      }

      client.track(event, keysToSnake({ ...properties }) as ApiObject, {
        traits: {
          ...(keysToSnake(traits) as ApiObject),
          ...metaTraits,
        },
      });
    } else if (Object.keys(metaTraits).length > 0) {
      client.track(event, keysToSnake(properties) as ApiObject, {
        traits: metaTraits,
      });
    } else {
      client.track(event, keysToSnake(properties) as ApiObject);
    }
  };

  const page = (event: string, pageProperties: object = {}) => {
    const client = getClient();
    if (!client) return;

    const properties = pageProperties as Record<string, unknown>;

    if (user) properties["userId"] = user.id;
    if (visitorCode) {
      properties["k_visitorCode"] = visitorCode;
    }

    const metaTraits = { ...getMetaTraits(), ...getOpprefTrait() };
    if (Object.keys(metaTraits).length > 0) {
      client.page(toSnake(event), properties as ApiObject, {
        traits: metaTraits,
      });
    } else {
      client.page(toSnake(event), properties as ApiObject);
    }
  };

  const identify = () => {
    const client = getClient();
    if (!client) return;

    const userIdentify =
      User.isLoggedIn() && User.hasUserData() && User.userData();

    if (userIdentify) {
      const { id, firstName, lastName, email, subscription, source } =
        userIdentify;
      const traits: IdentifyTraits = {
        id: String(id),
        firstName,
        lastName,
        email,
        source,
        trialStartDate: subscription?.trialStartedAt,
        trialEndDate: subscription?.trialEndsAt,
        subscriptionStartDate: subscription?.currentPeriodStartedAt,
        subscriptionPlanName: subscription?.plan?.name,
      };
      if (visitorCode) {
        traits["k_visitorCode"] = visitorCode;
      }
      client.identify(String(userIdentify.id), traits);
    }
  };

  // Pre-auth identify (e.g. share-lead email capture).
  const identifyAnonymous = (
    anonymousId: string,
    traits: IdentifyTraits = {},
  ) => {
    const client = getClient();
    if (!client || !anonymousId) return;
    client.identify(anonymousId, traits);
  };

  return { track, page, identify, identifyAnonymous };
};
